import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useSupabase } from '../../../hooks/useSupabase';

const ActivityFeed = ({ className = '' }) => {
  const { supabase } = useSupabase();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real activity data is now loaded from database

  useEffect(() => {
    const loadActivities = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get today's date range in German timezone (CET/CEST)
        const now = new Date();
        const germanTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Berlin"}));

        // Start of current day in German timezone
        const startOfDay = new Date(germanTime.getFullYear(), germanTime.getMonth(), germanTime.getDate());

        // End of current day in German timezone
        const endOfDay = new Date(germanTime.getFullYear(), germanTime.getMonth(), germanTime.getDate(), 23, 59, 59, 999);

        // Get recent time entries and filter for today's activities (based on clock times) with employee information
        // We'll filter records where either clock_in_time OR clock_out_time falls within today
        const { data: timeEntries, error: timeEntriesError } = await supabase
          .from('time_entries')
          .select(`
            id,
            employee_id,
            clock_in_time,
            clock_out_time,
            entry_type,
            notes,
            created_at,
            employees!inner(
              id,
              name,
              employee_code
            )
          `)
          .order('created_at', { ascending: false })
          .limit(100);

        // Filter entries to only include those with clock times from today (German timezone)
        const filteredEntries = timeEntries?.filter(entry => {
          const clockInTime = entry.clock_in_time ? new Date(entry.clock_in_time) : null;
          const clockOutTime = entry.clock_out_time ? new Date(entry.clock_out_time) : null;

          // Check if either clock time falls within today in German timezone
          const isClockInToday = clockInTime &&
            clockInTime >= startOfDay &&
            clockInTime <= endOfDay;

          const isClockOutToday = clockOutTime &&
            clockOutTime >= startOfDay &&
            clockOutTime <= endOfDay;

          return isClockInToday || isClockOutToday;
        }) || [];

        if (timeEntriesError) {
          throw timeEntriesError;
        }

        // Transform filtered time entries into activity feed format
        const transformedActivities = [];

        filteredEntries?.forEach(entry => {
          // Add clock-in activity
          if (entry.clock_in_time) {
            transformedActivities.push({
              id: `${entry.id}-clock-in`,
              employeeId: entry.employees?.employee_code,
              employeeName: entry.employees?.name,
              employeePhoto: null, // Could be added later if needed
              action: 'clock_in',
              timestamp: new Date(entry.clock_in_time),
              source: 'kiosk', // Default to kiosk, could be enhanced later
              location: 'Kiosk',
              note: entry.notes || null
            });
          }

          // Add clock-out activity if exists
          if (entry.clock_out_time) {
            transformedActivities.push({
              id: `${entry.id}-clock-out`,
              employeeId: entry.employees?.employee_code,
              employeeName: entry.employees?.name,
              employeePhoto: null,
              action: 'clock_out',
              timestamp: new Date(entry.clock_out_time),
              source: 'kiosk',
              location: 'Kiosk',
              note: entry.notes || null
            });
          }
        });

        // Sort by timestamp (most recent first)
        transformedActivities.sort((a, b) => b.timestamp - a.timestamp);

        // Take only the most recent 10 activities
        setActivities(transformedActivities.slice(0, 10));

      } catch (err) {
        console.error('Error loading activities:', err);
        setError(err.message || 'Failed to load activities');
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, []);

  const getActionIcon = (action) => {
    return action === 'clock_in' ? 'LogIn' : 'LogOut';
  };

  const getActionColor = (action) => {
    return action === 'clock_in' ? 'text-success' : 'text-warning';
  };

  const getSourceIcon = (source) => {
    const sourceMap = {
      kiosk: 'Monitor',
      manual: 'Edit',
      qr: 'QrCode'
    };
    return sourceMap?.[source] || 'Clock';
  };

  const getSourceLabel = (source) => {
    const sourceMap = {
      kiosk: 'Kiosk',
      manual: 'Manuell',
      qr: 'QR-Code'
    };
    return sourceMap?.[source] || 'Unbekannt';
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std`;
    
    return timestamp?.toLocaleDateString('de-DE');
  };

  if (isLoading) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-text-primary">Aktivitäten heute</h3>
          <Icon name="Activity" size={20} className="text-text-secondary" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div key={i} className="flex items-center space-x-3 animate-pulse">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <Icon name="User" size={20} className="text-text-secondary" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Aktivitäten heute</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-sm text-text-secondary">Live</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <span className="text-error text-sm">{error}</span>
          </div>
        </div>
      )}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">Keine Aktivitäten heute</p>
            <p className="text-sm text-text-secondary mt-1">Heutige Mitarbeiter-Zeiteinträge erscheinen hier</p>
          </div>
        ) : (
          activities?.map((activity) => (
          <div key={activity?.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
            <div className="relative">
              {activity?.employeePhoto ? (
                <Image
                  src={activity?.employeePhoto}
                  alt={activity?.employeeName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="User" size={20} className="text-text-secondary" />
                </div>
              )}
              <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card flex items-center justify-center ${getActionColor(activity?.action)} bg-card`}>
                <Icon name={getActionIcon(activity?.action)} size={10} />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-medium text-text-primary truncate">
                  {activity?.employeeName}
                </span>
                <span className="text-xs text-text-secondary">
                  {activity?.employeeId}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <span className={`capitalize ${getActionColor(activity?.action)}`}>
                  {activity?.action?.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="flex flex-col items-end space-y-1">
              <span className="text-xs text-text-secondary">
                {formatTime(activity?.timestamp)}
              </span>
              <div className="flex items-center space-x-1">
                <Icon name={getSourceIcon(activity?.source)} size={12} className="text-text-secondary" />
                <span className="text-xs text-text-secondary">
                  {getSourceLabel(activity?.source)}
                </span>
              </div>
            </div>
          </div>
        ))
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <button className="w-full text-sm text-primary hover:text-primary/80 transition-colors">
          Alle heutigen Aktivitäten anzeigen
        </button>
      </div>
    </div>
  );
};

export default ActivityFeed;